import { useEffect, useState, useCallback, forwardRef } from 'react';
import PropTypes from 'prop-types';

import { getClassNames, isNumeric, noOp } from '../../../../utils/util';

import './text-input.scss';

const TextInput = forwardRef(
  (
    {
      name,
      value,
      onChange,
      type,
      disabled,
      placeholder,
      numberOnly,
      auth,
      multiline,
      autoFocus,
      error,
      suggestions,
      onFocus,
      onBlur,
      icon,
      darkPlaceholder,
      onSuggestionClick,
      className,
      autoComplete,
    },
    ref
  ) => {
    const [showError, setShowError] = useState(error);
    const [sortedSuggestions, setSortedSuggestions] = useState([]);
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const sortSuggestions = useCallback(() => {
      if (!suggestions) return [];

      const copyOfSuggestions = [...suggestions];

      copyOfSuggestions.sort((suggestionOne, suggestionTwo) => {
        const suggestionOneTrimmed = suggestionOne.toLowerCase().trim();
        const suggestionTwoTrimmed = suggestionTwo.toLowerCase().trim();

        if (suggestionOneTrimmed < suggestionTwoTrimmed) return -1;
        if (suggestionOneTrimmed > suggestionTwoTrimmed) return 1;
        return 0;
      });

      return copyOfSuggestions;
    }, [suggestions]);

    useEffect(() => {
      setShowError(error);
    }, [error]);

    useEffect(() => {
      const sortedSuggestions = sortSuggestions();

      setSortedSuggestions(sortedSuggestions);
    }, [sortSuggestions]);

    const filterSuggestions = (value) => {
      const filteredSuggestions = sortedSuggestions.filter((suggestion) => {
        return suggestion.toLowerCase().includes(value.toLowerCase());
      });

      setFilteredSuggestions(filteredSuggestions);
    };

    const handleInputChange = (event) => {
      const { value } = event.target;

      if (suggestions) {
        filterSuggestions(value);
      }

      if (value === '' || !numberOnly) {
        setShowError(false);
        onChange(event);
        return;
      }

      if (isNumeric(value)) {
        setShowError(false);
        onChange(event);
      }
    };

    const handleSuggestionMouseDown = (suggestion) => {
      onChange({ target: { value: suggestion } });
      setShowSuggestions(false);
      onSuggestionClick(suggestion);
    };

    const handleFocus = () => {
      if (suggestions) {
        filterSuggestions(value);
        setShowSuggestions(true);
      }
      onFocus();
    };

    const handleBlur = () => {
      if (suggestions) {
        setShowSuggestions(false);
      }

      onBlur();
    };

    const renderSuggestions = () => {
      return filteredSuggestions.map((suggestion) => {
        return (
          <li
            className="text-input__suggestion"
            key={suggestion}
            onMouseDown={() => handleSuggestionMouseDown(suggestion)}
          >
            {suggestion}
          </li>
        );
      });
    };

    return (
      <div className={`${getClassNames('text-input', { auth, multiline, disabled, error: showError })} ${className}`}>
        {!multiline && (
          <>
            <input
              ref={ref}
              className={getClassNames('text-input__input', { auth, 'dark-placeholder': darkPlaceholder })}
              id={name}
              value={value}
              onChange={handleInputChange}
              type={type}
              disabled={disabled}
              placeholder={placeholder}
              autoFocus={autoFocus}
              onFocus={handleFocus}
              onBlur={handleBlur}
              autoComplete={autoComplete}
            />
            {icon}
          </>
        )}

        {multiline && (
          <textarea
            ref={ref}
            className={getClassNames('text-input__input', { auth, multiline })}
            id={name}
            value={value}
            onChange={handleInputChange}
            type={type}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete={autoComplete}
          />
        )}

        {showSuggestions && filteredSuggestions.length > 0 && (
          <ul className="text-input__suggestions">{renderSuggestions()}</ul>
        )}
      </div>
    );
  }
);

TextInput.propTypes = {
  name: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  numberOnly: PropTypes.bool,
  auth: PropTypes.bool,
  multiline: PropTypes.bool,
  autoFocus: PropTypes.bool,
  error: PropTypes.bool,
  suggestions: PropTypes.arrayOf(PropTypes.string),
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  icon: PropTypes.node,
  darkPlaceholder: PropTypes.bool,
  onSuggestionClick: PropTypes.func,
  className: PropTypes.string,
  autoComplete: PropTypes.string,
};

TextInput.defaultProps = {
  name: '',
  type: 'text',
  disabled: false,
  placeholder: '',
  numberOnly: false,
  auth: false,
  multiline: false,
  autoFocus: false,
  error: false,
  suggestions: [],
  onFocus: noOp,
  onBlur: noOp,
  icon: undefined,
  darkPlaceholder: false,
  onSuggestionClick: noOp,
  className: '',
  autoComplete: 'on',
};

export default TextInput;
